import "./index.scss";
import { CfCookieBanner, CookieData } from "./types";
import {
  CookieBanner as CookieBannerComponent,
  CookieBannerProps,
  TrackerOptions,
  cookiesAreAccepted,
} from "@wise-emotions/cookie-banner";
import { consentModeEvent, gtag, loadGtm, loadTag } from "./helpers/utils";
import { useSiteContext } from "context/SiteProvider";
import React, { useEffect } from "react";
import adFormInit from "./helpers/adForm";
import cn from "classnames";
import parse from "html-react-parser";

let gtmEventInit = false;
let gaEventInit = false;
let gaEventNeverAccepted = false;
let abtestingEventInit = false;
let adFormEventInit = false;
let pixelFbEventInit = false;
let twitterEventInit = false;
let linkedinEventInit = false;
let bingEventInit = false;
let nielsenEventInit = false;
let tikTokEventInit = false;
let clarityEventInit = false;
let newcoEventInit = false;
let pubmaticEventInit = false;
let weboramaEventInit = false;

const CookieBanner = ({
  __typename,
  contentful_id,
  bannerTitle,
  bannerText,
  bannerBtnAccept,
  bannerBtnReject,
  bannerBtnManage,
  modalTitle,
  modalSecondTitle,
  modalText,
  modalBtnAcceptAll,
  // modalBtnDenyAll,
  modalBtnClose,
  modalBtnSubmit,
  cookieTechTitle,
  cookieTechText,
  cookieTechToggleAreaLabel,
  cookieAnalysisTitle,
  cookieAnalysisText,
  cookieAnalysisToggleAreaLabel,
  cookieProfilingTitle,
  cookieProfilingText,
  cookieProfilingToggleAreaLabel,
  trackerGoogleAnalyticsTitle,
  trackerGoogleAnalyticsText,
  trackerABtestingTitle,
  trackerABtestingText,
  trackerAdformTitle,
  trackerAdformText,
  trackerPixelFbTitle,
  trackerPixelFbText,
  trackerTwitterTitle,
  trackerTwitterText,
  trackerBingTitle,
  trackerBingText,
  trackerLinkedinTitle,
  trackerLinkedinText,
  trackerNielsenTitle,
  trackerNielsenText,
  trackerTikTokTitle,
  trackerTikTokText,
  trackerClarityTitle,
  trackerClarityText,
  trackerNewcoTitle,
  trackerNewcoText,
  trackerPubmaticTitle,
  trackerPubmaticText,
  trackerWeboramaTitle,
  trackerWeboramaText,
}: CfCookieBanner) => {
  const { state, dispatch, lockScroll, releaseScroll } = useSiteContext();

  const consentMode = (accepted: boolean, hasChanged: boolean) => {
    if (accepted) {
      // Init state are accepted
      if (!gaEventInit) {
        consentModeEvent("default", true);

        // accepted and change
      } else if (hasChanged || !gaEventNeverAccepted) {
        consentModeEvent("update", accepted);

        gaEventNeverAccepted = true;
      }
      //  not accepted on change or init state
    } else if (hasChanged || !gaEventInit) {
      consentModeEvent(gaEventInit ? "update" : "default", accepted);
    }
  };

  const onTrackingEventGa = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !gaEventInit)) {
      loadTag({ name: "ga-init" });
      gaEventInit = true;
    }

    if (gtmEventInit && hasChanged) {
      consentMode(accepted, hasChanged);
    }
  };

  const onTrackingEventABtesting = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !abtestingEventInit)) {
      loadTag({ name: "abtesting-init" });
      abtestingEventInit = true;
    }
  };

  const onTrackingEventAdform = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !adFormEventInit)) {
      adFormInit();
      adFormEventInit = true;
    }
  };

  const onTrackingEventPixelFb = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !pixelFbEventInit)) {
      loadTag({ name: "fb-init" });
      pixelFbEventInit = true;
    }
  };

  const onTrackingEventTwitter = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !twitterEventInit)) {
      loadTag({ name: "twitter-init" });
      twitterEventInit = true;
    }
  };

  const onTrackingEventLinkedin = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !linkedinEventInit)) {
      loadTag({ name: "linkedin-init" });
      linkedinEventInit = true;
    }
  };

  const onTrackingEventBing = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !bingEventInit)) {
      loadTag({ name: "bing-init" });
      bingEventInit = true;
    }
  };

  const onTrackingEventNielsen = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !nielsenEventInit)) {
      loadTag({ name: "nielsen-init" });
      nielsenEventInit = true;
    }
  };

  const onTrackingEventTikTok = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !tikTokEventInit)) {
      loadTag({ name: "tiktok-init" });
      tikTokEventInit = true;
    }
  };

  const onTrackingEventClarity = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !clarityEventInit)) {
      loadTag({ name: "clarity-init" });
      clarityEventInit = true;
    }
  };

  const onTrackingEventNewco = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !newcoEventInit)) {
      loadTag({ name: "newco-init" });
      newcoEventInit = true;
    }
  };

  const onTrackingEventPubmatic = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !pubmaticEventInit)) {
      loadTag({ name: "pubmatic-init" });
      pubmaticEventInit = true;
    }
  };

  const onTrackingEventWeborama = (accepted: boolean, hasChanged: boolean) => {
    if (accepted && (hasChanged || !weboramaEventInit)) {
      loadTag({ name: "weborama-init" });
      weboramaEventInit = true;
    }
  };

  // NOTE: Analysis

  const clarityCookie: CookieData = {
    formName: "clarity",
    cookieTagName: "tp_clarity_cmp",
    title: trackerClarityTitle,
    paragraph: trackerClarityText?.trackerClarityText
      ? parse(trackerClarityText.trackerClarityText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) =>
      onTrackingEventClarity(accepted, hasChanged),
  };

  const aBtesting: CookieData = {
    formName: "abtesting",
    cookieTagName: "tp_abtesting_cmp",
    title: trackerABtestingTitle,
    paragraph: trackerABtestingText?.trackerABtestingText
      ? parse(trackerABtestingText.trackerABtestingText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) =>
      onTrackingEventABtesting(accepted, hasChanged),
  };

  const optionalCookiesAnalysis = [aBtesting, clarityCookie];
  const optionalCookiesAnalysisList: CookieData[] = [];
  optionalCookiesAnalysis.map(
    (optionalCookie) =>
      optionalCookie.title &&
      optionalCookie.paragraph &&
      optionalCookiesAnalysisList.push(optionalCookie),
  );

  // NOTE: Profiling
  const twitterCookie: CookieData = {
    formName: "twitter",
    cookieTagName: "twitter-init_cmp",
    title: trackerTwitterTitle,
    paragraph: trackerTwitterText?.trackerTwitterText
      ? parse(trackerTwitterText.trackerTwitterText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) => {
      onTrackingEventTwitter(accepted, hasChanged);
    },
  };

  const linkedinCookie: CookieData = {
    formName: "linkedin",
    cookieTagName: "linkedin-init_cmp",
    title: trackerLinkedinTitle,
    paragraph: trackerLinkedinText?.trackerLinkedinText
      ? parse(trackerLinkedinText.trackerLinkedinText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) => {
      onTrackingEventLinkedin(accepted, hasChanged);
    },
  };

  const bingCookie: CookieData = {
    formName: "bing",
    cookieTagName: "bing-init_cmp",
    title: trackerBingTitle,
    paragraph: trackerBingText?.trackerBingText
      ? parse(trackerBingText.trackerBingText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) => {
      onTrackingEventBing(accepted, hasChanged);
    },
  };

  const nielsenCookie: CookieData = {
    formName: "nielsen",
    cookieTagName: "nielsen-init_cmp",
    title: trackerNielsenTitle,
    paragraph: trackerNielsenText?.trackerNielsenText
      ? parse(trackerNielsenText.trackerNielsenText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) => {
      onTrackingEventNielsen(accepted, hasChanged);
    },
  };

  const tikTokCookie: CookieData = {
    formName: "tiktok",
    cookieTagName: "tiktok_init_cmp",
    title: trackerTikTokTitle,
    paragraph: trackerTikTokText?.trackerTikTokText
      ? parse(trackerTikTokText.trackerTikTokText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) => {
      onTrackingEventTikTok(accepted, hasChanged);
    },
  };

  const newcoCookie: CookieData = {
    formName: "newco",
    cookieTagName: "newco_init_cmp",
    title: trackerNewcoTitle,
    paragraph: trackerNewcoText?.trackerNewcoText
      ? parse(trackerNewcoText.trackerNewcoText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) => {
      onTrackingEventNewco(accepted, hasChanged);
    },
  };

  const pubmaticCookie: CookieData = {
    formName: "pubmatic",
    cookieTagName: "pubmatic_init_cmp",
    title: trackerPubmaticTitle,
    paragraph: trackerPubmaticText?.trackerPubmaticText
      ? parse(trackerPubmaticText.trackerPubmaticText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) => {
      onTrackingEventPubmatic(accepted, hasChanged);
    },
  };

  const weboramaCookie: CookieData = {
    formName: "weborama",
    cookieTagName: "weborama_init_cmp",
    title: trackerWeboramaTitle,
    paragraph: trackerWeboramaText?.trackerWeboramaText
      ? parse(trackerWeboramaText.trackerWeboramaText)
      : "",
    onLoadTracker: ({ accepted, hasChanged }) => {
      onTrackingEventWeborama(accepted, hasChanged);
    },
  };

  // NOTE: management of optionalCookies
  const optionalCookies = [
    twitterCookie,
    linkedinCookie,
    bingCookie,
    nielsenCookie,
    tikTokCookie,
    newcoCookie,
    pubmaticCookie,
    weboramaCookie,
  ];
  const optionalCookiesList: CookieData[] = [];
  optionalCookies.map(
    (optionalCookie) =>
      optionalCookie.title &&
      optionalCookie.paragraph &&
      optionalCookiesList.push(optionalCookie),
  );

  const defaultProps: CookieBannerProps & { gtmAccount: string } = {
    domain:
      // NOTE: put key domain only when application uses .telepass.com
      process.env.GATSBY_ENV === "development" ||
      process.env.GATSBY_ENV === "test" ||
      process.env.GATSBY_ENV === "production"
        ? "telepass.com"
        : undefined,
    gtmAccount: process.env.GATSBY_GTM,
    bannerLabels: {
      accept: bannerBtnAccept,
      reject: bannerBtnReject || "Solo necessari",
      manage: bannerBtnManage,
      paragraph: bannerText?.bannerText ? parse(bannerText.bannerText) : "",
      title: bannerTitle,
    },
    modalLabels: {
      title: modalTitle,
      secondTitle: modalSecondTitle,
      paragraph: modalText?.modalText ? parse(modalText.modalText) : "",
      cancel: modalBtnClose,
      submit: modalBtnSubmit,
      // denyAllLabel: modalBtnDenyAll,
      acceptAllLabel: modalBtnAcceptAll,
    },
    sections: [
      {
        toggle: cookieTechToggleAreaLabel,
        toggleFormName: "toggle-mandatory",
        mandatory: true,
        title: cookieTechTitle,
        paragraph: cookieTechText?.cookieTechText
          ? parse(cookieTechText.cookieTechText)
          : "",
      },
      {
        toggle: cookieAnalysisToggleAreaLabel,
        toggleFormName: "toggle-analysis",
        title: cookieAnalysisTitle,
        paragraph: cookieAnalysisText?.cookieAnalysisText
          ? parse(cookieAnalysisText.cookieAnalysisText)
          : "",
        trackers: [
          {
            formName: "ga",
            cookieTagName: "tp_ga_cmp",
            title: trackerGoogleAnalyticsTitle,
            paragraph: trackerGoogleAnalyticsText?.trackerGoogleAnalyticsText
              ? parse(trackerGoogleAnalyticsText.trackerGoogleAnalyticsText)
              : "",
            isAnonymous: true,
            onLoadTracker: ({ accepted, hasChanged }) =>
              onTrackingEventGa(accepted, hasChanged),
          },
          ...(optionalCookiesAnalysisList as TrackerOptions[]),
        ],
      },
      {
        toggle: cookieProfilingToggleAreaLabel,
        toggleFormName: "toggle-profiling",
        title: cookieProfilingTitle,
        paragraph: cookieProfilingText?.cookieProfilingText || "",
        trackers: [
          {
            formName: "adform",
            cookieTagName: "tp_adform_cmp",
            title: trackerAdformTitle,
            paragraph: trackerAdformText?.trackerAdformText
              ? parse(trackerAdformText.trackerAdformText)
              : "",
            onLoadTracker: ({ accepted, hasChanged }) => {
              onTrackingEventAdform(accepted, hasChanged);
            },
          },
          {
            formName: "pixelFb",
            cookieTagName: "tp_pixelFb_cmp",
            title: trackerPixelFbTitle ?? "",
            paragraph: trackerPixelFbText?.trackerPixelFbText
              ? parse(trackerPixelFbText.trackerPixelFbText)
              : "",
            onLoadTracker: ({ accepted, hasChanged }) => {
              onTrackingEventPixelFb(accepted, hasChanged);
            },
          },
          ...(optionalCookiesList as TrackerOptions[]),
        ],
      },
    ],
  };

  const close = () => {
    if (releaseScroll && dispatch) {
      releaseScroll();
      if (cookiesAreAccepted()) {
        dispatch({ type: "write_script_gtm", payload: true });
      }
    }
  };

  const open = () => {
    if (lockScroll) {
      lockScroll();
    }
  };

  const onSaveCookie = (refresh: boolean) => {
    if (refresh) {
      window.location.reload();
    }
    close();
  };

  useEffect(() => {
    if (!gtmEventInit) {
      loadGtm(process.env.GATSBY_GTM);
      gtmEventInit = true;
    }
    consentMode(false, false);
    // NOTE: cookie are accepted
    if (cookiesAreAccepted() && dispatch) {
      dispatch({ type: "write_script_gtm", payload: true });
    }
  }, [dispatch]);

  if (!state?.scriptCookieGtm) {
    return (
      <div className={cn("cookie-banner")}>
        <CookieBannerComponent
          onDecisionTaken={onSaveCookie}
          onModalDismissed={close}
          onModalOpen={open}
          {...defaultProps}
        />
      </div>
    );
  }
  return null;
};

export default CookieBanner;
